import {
  faExclamationCircle,
  faSearch,
  faCircleXmark,
  faArrowDownShortWide,
  faPlusCircle,
  faEllipsis,
  faFolderPlus,
  faArchive,
  faFileImport,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useMemo } from 'react'
import {
  useNavigate,
  useOutletContext,
  Navigate,
  generatePath,
} from 'react-router-dom'
import AnnouncementAlert from '../../components/dashboard/AnnouncementAlert.jsx'
import DashboardWrapper from '../../components/dashboard/DashboardWrapper'
import { Button } from '../../components/button'
import { openUpgradeDialog } from '../../components/UpgradeDialog.tsx'
import {
  ALPHABETICAL,
  CREATED_NEW_OLD,
  CREATED_OLD_NEW,
  RUNDOWN_START_TIME,
} from '../../constants/sortTypes.js'
import RundownCard from '../../components/dashboard/partials/RundownCard.jsx'
import EventCard from '../../components/dashboard/partials/EventCard.jsx'
import Fuse from 'fuse.js'
import { Menu, MenuItem } from '../../components/interactives/dropdown-menu.tsx'
import { CsvImportRoutes } from '../../app/index.js'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../components/interactives/Tooltip.jsx'
import { useUserEvents } from '../../entities/event/lib/use-user-events.ts'
import {
  DashboardPermissionGuard,
  useDashboardPermission,
  DashboardPermissions,
} from '../../entities/user/index.ts'
import { UserRole, Features } from '~shared/types'
import { ENVIRONMENT } from '../../shared/environment'

export default function DashboardMain() {
  const { user, team, teams, plan, rundownList, eventList } = useOutletContext()
  const [searchText, setSearchText] = useState('')
  const [sortedBy, setSortedBy] = useState(RUNDOWN_START_TIME)
  const [rundownTab, setRundownTab] = useState('rundowns')

  const navigate = useNavigate()

  const isTeamEditor = useDashboardPermission({
    permissions: DashboardPermissions.TEAM_EDIT,
  })

  const sortItems = (items, sortBy) => {
    return items.slice().sort((a, b) => {
      switch (sortBy) {
        case RUNDOWN_START_TIME:
          return new Date(a.startTime) < new Date(b.startTime) ? 1 : -1
        case CREATED_NEW_OLD:
          return new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1
        case CREATED_OLD_NEW:
          return new Date(a.createdAt) > new Date(b.createdAt) ? 1 : -1
        default: {
          const _A = a.name.toUpperCase()
          const _B = b.name.toUpperCase()
          return _A < _B ? -1 : _A > _B ? 1 : 0
        }
      }
    })
  }

  const userEvents = useUserEvents({
    teamId: team?.id,
  })

  const sortedEvents = useMemo(() => {
    if (!Array.isArray(userEvents)) return []
    return sortItems(
      userEvents.filter((e) => e.archivedAt == null),
      sortedBy,
    )
  }, [sortedBy, userEvents])

  const sortedRundowns = useMemo(() => {
    if (!Array.isArray(rundownList)) return []
    return sortItems(
      rundownList.filter((r) => r.archivedAt == null),
      sortedBy,
    )
  }, [rundownList, sortedBy])

  const filterItems = (items, searchText) => {
    const fuse = new Fuse(items, { keys: ['name'] })
    return searchText
      ? fuse.search(searchText).map((result) => result.item)
      : items
  }

  const filteredEvents = useMemo(
    () => filterItems(sortedEvents, searchText),
    [searchText, sortedEvents],
  )
  const filteredRundowns = useMemo(
    () =>
      filterItems(sortedRundowns, searchText).filter(
        (rundown) =>
          !rundown.eventId &&
          (rundownTab === 'rundowns'
            ? rundown.status !== 'template'
            : rundown.status === 'template'),
      ),
    [searchText, sortedRundowns, rundownTab],
  )

  if (!Array.isArray(eventList)) {
    if (teams?.length) {
      const firstUserTeamId = teams[0].id

      return <Navigate to={`/dashboard/${firstUserTeamId}`} />
    }

    return <Navigate to="/dashboard" />
  }

  function withinLimit(current, limit) {
    if (limit === -1) return true
    if (current < limit) return true
    return false
  }

  async function handleOnCreateRundown() {
    if (withinLimit(rundownList.length, plan?.limits?.rundowns))
      return navigate('/create')
    openUpgradeDialog("You've reached your limit for rundowns.")
  }

  // if (!team) return <DashboardCheck />

  return (
    <DashboardWrapper
      data-label="layout-dashboard"
      title={
        <>
          <AnnouncementAlert
            location="dashboard"
            className="text-base mb-8"
          />
          <div className="flex flex-wrap gap-4">
            <p className="grow">Dashboard</p>
            <div className="flex flex-wrap gap-x-6 gap-y-4">
              <div className="flex">
                <Button
                  className="text-base rounded-r-none focus:z-10"
                  text="New rundown"
                  icon={faPlusCircle}
                  colour="dark"
                  onClick={handleOnCreateRundown}
                  data-label="create-rundown-button"
                  disabled={!isTeamEditor}
                />

                <Tooltip>
                  <TooltipContent>Create new event</TooltipContent>
                  <TooltipTrigger>
                    <Button
                      className="border-l-2 border-gray-800 text-base !rounded-none focus:z-10"
                      icon={faFolderPlus}
                      colour="dark"
                      onClick={() => {
                        if (withinLimit(eventList.length, plan?.limits?.events))
                          return navigate('/create-event')
                        openUpgradeDialog(
                          "You've reached your limit for events.",
                        )
                      }}
                      disabled={!isTeamEditor}
                    />
                  </TooltipTrigger>
                </Tooltip>
                <Tooltip>
                  <TooltipContent>Import CSV file</TooltipContent>
                  <TooltipTrigger>
                    <Button
                      className="border-l-2 border-gray-800 text-base rounded-l-none"
                      icon={faFileImport}
                      colour="dark"
                      onClick={() => {
                        if (!plan?.features?.includes(Features.IMPORT))
                          return openUpgradeDialog(
                            'Importing CSV files is not included in your plan.',
                          )
                        void navigate(
                          generatePath(CsvImportRoutes.MAIN, {
                            teamId: team.id,
                          }),
                        )
                      }}
                      disabled={!isTeamEditor}
                    />
                  </TooltipTrigger>
                </Tooltip>
              </div>
            </div>
          </div>
        </>
      }
    >
      <>
        {!user?.emailVerified ? (
          <div className="px-4 py-2 mb-4 flex flex-col gap-4 sm:flex-row justify-between items-center bg-white/10 rounded-sm">
            <div className="flex gap-2 items-center">
              <FontAwesomeIcon icon={faExclamationCircle} />
              <span>Please verify your email address before continuing.</span>
            </div>
            <Button
              text="Verify my email"
              onClick={() => navigate('/verify')}
            />
          </div>
        ) : (
          ''
        )}

        <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-between">
          <span className="flex-1"></span>
          <div className="pr-3 bg-gray-100/5 focus-within:bg-gray-100/10 focus-within:ring-3 rounded-sm w-full sm:w-min whitespace-nowrap flex items-center justify-between">
            <input
              placeholder="Find event or rundown"
              className="px-3 h-8 bg-transparent focus:outline-hidden ring-transparent"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value)
              }}
            />
            {searchText ? (
              <button
                className="h-7 focus:outline-hidden focus:ring-3 rounded-sm"
                onClick={() => setSearchText('')}
              >
                <FontAwesomeIcon icon={faCircleXmark} />
              </button>
            ) : (
              <FontAwesomeIcon icon={faSearch} />
            )}
          </div>

          <Menu
            label="Sort"
            icon={faArrowDownShortWide}
          >
            <MenuItem
              label="Rundown start time"
              onClick={() => setSortedBy(RUNDOWN_START_TIME)}
              active={sortedBy === RUNDOWN_START_TIME}
            />
            <MenuItem
              label="Created (latest)"
              onClick={() => setSortedBy(CREATED_NEW_OLD)}
              active={sortedBy === CREATED_NEW_OLD}
            />
            <MenuItem
              label="Created (oldest)"
              onClick={() => setSortedBy(CREATED_OLD_NEW)}
              active={sortedBy === CREATED_OLD_NEW}
            />
            <MenuItem
              label="Alphabetical"
              onClick={() => setSortedBy(ALPHABETICAL)}
              active={sortedBy === ALPHABETICAL}
            />
          </Menu>

          <Menu icon={faEllipsis}>
            <MenuItem
              icon={faArchive}
              label="Show archive"
              onClick={() => navigate('archive')}
              disabled={!isTeamEditor}
            />
          </Menu>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {/* EVENTS */}
          <div className="flex col-span-full items-center gap-2">
            <div className="w-4 bg-gray-800 h-0.5"></div>
            <p className="text-sm text-gray-600">Events</p>
            <div className="grow bg-gray-800 h-0.5"></div>
          </div>

          {filteredEvents.length === 0 ? (
            <div className="h-[8rem] border border-dashed border-gray-800 text-gray-500 rounded-sm flex flex-col justify-center text-center">
              No events found
            </div>
          ) : (
            filteredEvents.map((event) => {
              const eventRundowns = rundownList.filter(
                (rundown) => rundown.eventId === event.id,
              )
              return (
                <EventCard
                  key={event.id}
                  event={event}
                  rundowns={eventRundowns.filter(
                    (rundown) => !rundown.archivedAt,
                  )}
                  archivedCount={
                    eventRundowns.filter((rundown) => rundown.archivedAt).length
                  }
                />
              )
            })
          )}

          <DashboardPermissionGuard role={UserRole.TEAM_VIEWER}>
            <div className="flex col-span-full items-center gap-2">
              <div className="w-4 bg-gray-800 h-0.5"></div>
              <button
                className={[
                  'text-sm cursor-pointer',
                  rundownTab === 'rundowns'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-400',
                ].join(' ')}
                onClick={() => setRundownTab('rundowns')}
              >
                Rundowns
              </button>
              <div className="w-4 bg-gray-800 h-0.5"></div>
              <button
                className={[
                  'text-sm cursor-pointer',
                  rundownTab === 'templates'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-400',
                ].join(' ')}
                onClick={() => setRundownTab('templates')}
              >
                Templates
              </button>
              <div className="grow bg-gray-800 h-0.5"></div>
            </div>

            {/* RUNDOWNS */}

            {filteredRundowns.length === 0 ? (
              <div className="h-[8rem] border border-dashed border-gray-800 text-gray-500 rounded-sm flex flex-col justify-center text-center">
                No{' '}
                {rundownTab === 'templates' ? (
                  <p>
                    templates found.
                    <br />{' '}
                    <a
                      href={`${ENVIRONMENT.marketingLinks.docs}/rundown/templates/`}
                      className="text-blue-500 hover:text-blue-400 text-sm"
                    >
                      Learn how to create a template.
                    </a>
                  </p>
                ) : (
                  'rundowns found'
                )}
              </div>
            ) : (
              filteredRundowns.map((rundown) => (
                <RundownCard
                  key={rundown.id}
                  rundown={rundown}
                  eventList={eventList}
                />
              ))
            )}
          </DashboardPermissionGuard>
        </div>
      </>
    </DashboardWrapper>
  )
}
